/**
 * Base API client for centralized HTTP requests with authentication
 * Follows the foundation patterns established for the new Mantine UI
 */

import { msalInstance, acquireBearerToken } from '../auth';
import { ApiError, AuthenticationError, NetworkError, ValidationError } from './errors';

const API_VERSION = import.meta.env.VITE_API_VERSION || 'v3';
const BASE_API_URL = import.meta.env.VITE_API_BASE_URL || window.location.origin;
const BASE_URL = `${BASE_API_URL}/api/${API_VERSION}`;

export class ApiClient {
  private baseURL: string;
  private version: string;

  constructor(baseURL: string = BASE_URL, version: string = API_VERSION) {
    this.baseURL = baseURL;
    this.version = version;
  }

  /**
   * Make an authenticated HTTP request
   */
  async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    try {
      const bearerToken = await acquireBearerToken(msalInstance);

      if (!bearerToken) {
        throw new AuthenticationError('Authentication required');
      }

      const headers = {
        'Content-Type': 'application/json',
        'Authorization': bearerToken,
        ...options.headers,
      };

      const response = await fetch(`${this.baseURL}${endpoint}`, {
        ...options,
        headers,
      });

      // Handle response
      return await this.handleResponse<T>(response);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Handle HTTP response and convert to appropriate format
   */
  private async handleResponse<T>(response: Response): Promise<T> {
    // Handle different status codes
    if (response.status === 401) {
      throw new AuthenticationError('Authentication required');
    }

    if (response.status === 404) {
      throw new ApiError('Resource not found', 404, 'NOT_FOUND');
    }

    if (response.status === 403) {
      throw new ApiError('Access forbidden', 403, 'FORBIDDEN');
    }

    if (response.status === 500) {
      throw new ApiError('Internal server error', 500, 'INTERNAL_SERVER_ERROR');
    }

    if (response.status === 504) {
      throw new NetworkError('Connection timeout');
    }

    if (!response.ok) {
      let errorMessage = 'Unknown error occurred';
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorMessage;
      } catch {
        // If response isn't JSON, use status text
        errorMessage = response.statusText || errorMessage;
      }

      if (response.status >= 400 && response.status < 500) {
        throw new ValidationError(errorMessage, { status: response.status });
      }

      throw new ApiError(errorMessage, response.status);
    }

    // Parse JSON response
    try {
      return await response.json();
    } catch (error) {
      throw new ApiError('Invalid JSON response', response.status, 'INVALID_RESPONSE');
    }
  }

  /**
   * Handle request errors
   */
  private handleError(error: unknown): Error {
    if (error instanceof ApiError) {
      return error;
    }

    if (error instanceof TypeError && error.message.includes('fetch')) {
      return new NetworkError('Network connection failed');
    }

    if (error instanceof Error) {
      return new ApiError(error.message);
    }

    return new ApiError('Unknown error occurred');
  }

  /**
   * Convenience methods for common HTTP methods
   */
  async get<T>(endpoint: string, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  async post<T>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async patch<T>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(endpoint: string, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }
}

// Export singleton instance
export const apiClient = new ApiClient();
